import React, { ReactElement, useEffect, useState } from 'react';
import Buttons from '@components/Buttons';
import useNoodleApi from '@hooks/useNoodleApi';
import { deserializeHtml } from '@components/SlateEditor';
import Spacer from '@components/Spacer';
import FieldLabel from '@components/FormFields/FieldLabel';
import { Descendant } from 'slate';
import createRichTextAst from '@helpers/createRichTextAst';
import ProgressIndicator from '@components/ProgressIndicator';
import InputComposer from '@components/InputComposer';
import updateWorkflowStep from '@tsClient/workflows/updateWorkflowStep';
import createWorkflowStep from '@tsClient/workflows/createWorkflowStep';
import { MESSAGE_ATTACHMENT_REFERENCE_TYPES, SlateNode, StepType, WORKFLOW_ACTIONS } from '@typings/api-models';
import s from './CreateEditSendMessageActionForm.module.scss';

export type SendMessageActionData = {
  message: {
    attachments: { referenceId: string; referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES; title: string; id: string; }[];
    slate: SlateNode[],
    html: string,
    text: string,
    medias: { id: string; name: string; }[],
    handbooks: { id: string }[],
  },
};

type Props = {
  actionId?: string | null;
  messageData?: SendMessageActionData | null;
  workflowId: string;
  onSave: () => void;
};

type Values = {
  attachments: { referenceId: string; referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES; title: string; id: string; }[];
  slate: { children: Descendant[] }[] | null;
  media: { id: string; name: string; }[];
  files: (File | string)[];
  handbooks: { id: string }[];
};

const CreateEditSendMessageActionForm = ({
  actionId,
  messageData,
  workflowId,
  onSave,
}: Props): ReactElement => {
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const [values, setValues] = useState<Values>({
    attachments: messageData?.message?.attachments || [],
    files: [],
    handbooks: messageData?.message?.handbooks || [],
    media: messageData?.message?.medias || [],
    slate: messageData?.message ? null : [createRichTextAst('')],
  });

  const handleSlateChange = (slate: { children: Descendant[] }[]): void => {
    setValues(val => ({ ...val, slate }));
  };
  const handleMediaChange = (media: { id: string; name?: string; uploadPercentage?: number }[]): void => {
    setValues(val => ({
      ...val,
      media: media.map((m) => ({
        ...m,
        name: m.name || 'media',
      })),
    }));
  };
  const handleHandbookChange = (handbooks: { id: string }[]): void => {
    setValues(val => ({ ...val, handbooks }));
  };

  const handleAttachmentChange = (attachments: {
    referenceId: string;
    referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES;
    title: string;
    id: string;
  }[]): void => {
    setValues(val => ({ ...val, attachments }));
  };

  const { getData: updateWorkflowStepFn } = useNoodleApi(updateWorkflowStep, { toastOnError: true });
  const { getData: createWorkflowStepFn } = useNoodleApi(createWorkflowStep, { toastOnError: true });

  useEffect(() => {
    if (messageData?.message && messageData?.message.html) {
      const document = new DOMParser().parseFromString(messageData?.message.html, 'text/html');
      setValues(oldValues => ({
        ...oldValues,
        slate: deserializeHtml(document.body) as { children: Descendant[] }[],
      }));
    }
    setIsInitialized(true);
  }, [messageData?.message]);

  if (!isInitialized) {
    return <ProgressIndicator isCentered/>;
  }

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    const sendMessageData = {
      attachments: values.attachments,
      handbooks: values.handbooks,
      medias: values.media,
      slate: values.slate,
    };
    if (actionId) {
      await updateWorkflowStepFn({
        id: workflowId,
        stepId: actionId,
        updates: {
          data: { message: sendMessageData },
          dependentOn: [],
        },
      });
    } else {
      await createWorkflowStepFn({
        id: workflowId,
        stepData: {
          data: { message: sendMessageData },
          dependentOn: [],
          eventName: WORKFLOW_ACTIONS.SEND_MESSAGE,
          type: StepType.ACTION,
        },
      });
    }
    onSave();
  };

  return (
    <>
      <div className={s.craftWelcomeMessageForm}>
        <FieldLabel id="description">
          Message Body
          <Spacer size={16}/>
          <InputComposer
            isRichText
            rows={3}
            showAddVideo
            showAddAnyFile
            showAddDocumentRequest
            showAddFormRequest
            showAddPaymentLink
            showAddInvoice
            showSendButton={false}
            onSend={handleSave}
            onChangeMedia={handleMediaChange}
            onChangeHandbooks={handleHandbookChange}
            onChangeText={handleSlateChange}
            onChangeAttachments={handleAttachmentChange}
            showProTips={false}
            initialText={values.slate}
            initialAttachments={values.attachments}
            initialMedia={values.media}
            isFetching={false}
            placeholder='Type your message'
            showAddHandbook
            referenceId={'workflowId'}
            referenceType={'action-message'}
          />
        </FieldLabel>
        <Buttons
          isFetching={isSaving}
          isFullWidth
          isSecondary
          onClick={handleSave}
        >
          {actionId ? 'Update Message' : 'Create Message'}
        </Buttons>
      </div>
    </>
  );
};

export default CreateEditSendMessageActionForm;
